import { z } from "zod";
import { stableUploadUnavailable } from "../validation.js";
const RunStableTransferHardDeleteInputSchema = z
    .object({ limit: z.number().int().min(1).max(200).default(25) })
    .strict();
/**
 * The genuinely irreversible, high-consequence hard-delete worker
 * (`docs/products/stable.md`: "After `inactive_until`, an idempotent worker
 * hard-deletes the origin Stable's Sealed rows and any associated private
 * files for that horse, leaving a minimal non-Sealed tombstone for audit
 * and restore safety"). Not yet wired to a scheduled worker — see the
 * completion report, matching the same not-yet-scheduled precedent every
 * other Phase's own scan/expiry job shipped in (`runVaccinationAlertScan`,
 * `runAccountingAutoInvoiceScan`, `expireOwnerLinkRequests`). Unlike those,
 * this operation is destructive and cannot be replayed away by a
 * self-correcting recheck — so this function itself, and the two
 * repository calls it drives, are held to the same rigor as Phase 7/9's
 * immutability triggers: explicit, auditable, and safe to run twice or on
 * data that is not yet due.
 *
 * Ordering (per `hardDeleteStableTransferOriginRecords`'s doc comment in
 * `contracts.ts`): for each due transfer, private files are deleted from
 * object storage *before* any database row is touched, then the DB
 * transaction runs and stamps `origin_records_deleted_at`. This order means
 * a crash between the two steps can only leave already-deleted files with
 * their DB rows not yet cleaned up (safe to retry — `deleteObject` is
 * idempotent and the DB step is re-selected next run), never the reverse
 * (DB rows gone but files orphaned and never retried again).
 *
 * If `documentUploads` is not configured in this environment, file deletion
 * is skipped for that run (matching `documentUploads` being optional
 * elsewhere in `StableDependencies`) — but the DB-side deletion still
 * requires it in order to keep the "files before DB rows" ordering honest,
 * so this function fails closed (`stableUploadUnavailable`) rather than
 * silently deleting database rows while leaving files behind. A deployment
 * that has private Sealed documents at all must configure object storage;
 * one with none configured never has documents to delete either
 * (`documentObjectKeys` will always be empty), and the worker degrades to
 * pure row deletion in that case without ever reaching the failure path.
 */
export async function runStableTransferHardDelete(input, dependencies) {
    const parsed = RunStableTransferHardDeleteInputSchema.parse(input);
    const now = dependencies.clock.now();
    const due = await dependencies.repository.listStableTransfersDueForOriginDeletion({
        now,
        limit: parsed.limit,
    });
    let deletedCount = 0;
    let alreadyDeletedCount = 0;
    let filesDeletedCount = 0;
    for (const transfer of due) {
        if (transfer.documentObjectKeys.length > 0) {
            const uploads = dependencies.documentUploads;
            if (uploads === undefined)
                throw stableUploadUnavailable();
            for (const objectKey of transfer.documentObjectKeys) {
                await uploads.deleteObject({ objectKey });
                filesDeletedCount += 1;
            }
        }
        const outcome = await dependencies.repository.hardDeleteStableTransferOriginRecords({
            transferId: transfer.transferId,
            horseId: transfer.horseId,
            now,
        });
        if (outcome === "DELETED")
            deletedCount += 1;
        if (outcome === "ALREADY_DELETED")
            alreadyDeletedCount += 1;
    }
    return {
        consideredCount: due.length,
        deletedCount,
        alreadyDeletedCount,
        filesDeletedCount,
    };
}
